/**
 * FormConsentField is a checkbox-based consent field for forms.
 * It includes optional description and URL link sections.
 */

import { type InputHTMLAttributes } from 'react';
import { useNode, type Node, ROOT_NODE, useEditor } from '@craftjs/core';
import { css, cx } from '@emotion/css';
import type { NodeCustomProps } from '../../../types/craft';
import { useRenderContext } from '../../../context/RenderContext';
import { FORM_CONSENT_FIELD_NODE_NAME } from '../../../utils/constants';
import { generateUuidV4 } from '../form-utils';
import { getBreakpointProp } from '../../../utils/breakpointUtils';
import { FormConsentFieldPropsDto } from '../../../dto/craft/props/FormConsentFieldPropsDto';

export type FormConsentFieldProps = FormConsentFieldPropsDto;

export const defaultProps: FormConsentFieldProps = {
  id: '',
  label: 'Consent',
  inputType: 'consent',
  text: 'Yes, I consent to receiving emails',
  required: false,
  hidden: false,
  selectedByDefault: false,
  showDescription: true,
  description:
    'This form collects information we will use to send you updates about promotions, special offers, and news. We will not share or sell your personal information. You can unsubscribe at any time.',
  showUrlLink: false,
  linkUrl: '',
  linkText: '',
  selectedLinkVariant: 'link1',
};

const FormConsentField = (props: Partial<FormConsentFieldProps>) => {
  const mergedProps: FormConsentFieldProps = { ...defaultProps, ...props };
  const { isEditable, environment, textVariants } = useRenderContext();

  const {
    id: craftNodeId,
    isLocked,
    connectors: { connect, drag },
  } = useNode((node: Node) => ({
    isLocked: node.data.custom?.isLocked,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Generate a unique ID if one isn't set
  if (!mergedProps.id) {
    const fieldId = generateUuidV4();
    editorActions.history.ignore().setProp(craftNodeId, (props: FormConsentFieldProps) => {
      props.id = fieldId;
    });
    mergedProps.id = fieldId;
  }

  const required = getBreakpointProp(mergedProps, 'required', breakpoint);
  const hidden = getBreakpointProp(mergedProps, 'hidden', breakpoint);
  const selectedByDefault = getBreakpointProp(mergedProps, 'selectedByDefault', breakpoint);
  const text = getBreakpointProp(mergedProps, 'text', breakpoint);
  const showDescription = getBreakpointProp(mergedProps, 'showDescription', breakpoint);
  const description = getBreakpointProp(mergedProps, 'description', breakpoint);
  const showUrlLink = getBreakpointProp(mergedProps, 'showUrlLink', breakpoint);
  const linkUrl = getBreakpointProp(mergedProps, 'linkUrl', breakpoint);
  const linkText = getBreakpointProp(mergedProps, 'linkText', breakpoint);
  const selectedLinkVariant = getBreakpointProp(mergedProps, 'selectedLinkVariant', breakpoint);

  const isServerRender = environment === 'server';
  const disablePointerEvents = isEditable && isLocked;
  const checkboxId = mergedProps.id;

  const checkboxProps = buildCheckboxProps(
    required,
    selectedByDefault,
    'field-checkbox',
    isServerRender,
    checkboxId,
    showDescription,
    description,
  );

  // Generate link variant class from global styles
  const linkVariantClass = textVariants({ variant: `link.${selectedLinkVariant}` });

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx(
        'field-node',
        'field-node--consent',
        isEditable && 'node-dashed-indicator styled-node',
        'boundary-indicator',
        disablePointerEvents && 'pointer-events-none',
        hidden && 'field-node--hidden',
      )}
      data-field-id={mergedProps.id}
      aria-hidden={hidden || undefined}
    >
      {/* Description section */}
      {showDescription && description && (
        <p id={`${checkboxId}-description`} className="field-consent-description">
          {description}
        </p>
      )}

      {/* URL Link section */}
      {showUrlLink && linkUrl && linkText && (
        <div className="field-link-wrapper">
          <a
            href={ensureUrlProtocol(linkUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className={cx('field-link', linkVariantClass)}
          >
            <span>{linkText}</span>
          </a>
        </div>
      )}

      {/* Checkbox + text section */}
      <div className="field-checkbox-wrapper">
        <input id={mergedProps.id} key={selectedByDefault.toString()} {...checkboxProps} />
        <label htmlFor={mergedProps.id} className="field-checkbox-text">
          {text}
          {required && (
            <span aria-hidden="true" className="field-required-indicator">
              *
            </span>
          )}
        </label>
      </div>

      {/* Helper text for validation errors */}
      <div className={cx('field-helper-text-wrapper', css({ display: 'none' }))}>
        <p id={`${mergedProps.id}-helper-text`} className="field-helper-text">
          {'\u200B'}
        </p>
      </div>
    </div>
  );
};

/**
 * Ensures a URL has a protocol (http:// or https://).
 * If the URL already has a protocol, returns it unchanged.
 * Otherwise, prepends https:// to the URL.
 */
export function ensureUrlProtocol(url: string): string {
  if (!url) return url;
  // Check if URL already has a protocol
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  // If no protocol, add https://
  return `https://${url}`;
}

/**
 * Builds checkbox attributes for the input element.
 */
function buildCheckboxProps(
  required: boolean,
  selectedByDefault: boolean,
  className: string,
  isServerRender: boolean,
  checkboxId: string,
  showDescription: boolean,
  description: string,
): InputHTMLAttributes<HTMLInputElement> {
  const attributes: InputHTMLAttributes<HTMLInputElement> = {
    name: checkboxId,
    type: 'checkbox',
    className,
    required,
    'aria-required': required,
    'aria-describedby': showDescription && description ? `${checkboxId}-description` : undefined,
    // Use defaultChecked for both editor and client - only Inspector toggle controls the value
    ...(isServerRender
      ? selectedByDefault && { checked: true }
      : selectedByDefault && { defaultChecked: true }),
  };

  (attributes as Record<string, unknown>)['data-required'] = required || undefined;

  return attributes;
}

FormConsentField.craft = {
  displayName: FORM_CONSENT_FIELD_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Consent',
    isLocked: false,
  } as NodeCustomProps,
};

export default FormConsentField;
